import { Portrait, PortraitConfig } from "./portrait";
import container from "../../ioc/inversify.config";
import TYPES from "../../ioc/types";
import { ThemeManager } from "../design-system/theme-manager";
import { buildTextStyle } from "../utils/text-style";

/**
 * DialogPanel - Component for the new event-based dialog system
 * Displays dialog messages with speaker names, text, and choice options
 */
export class DialogPanel {
    private scene: Phaser.Scene;
    private container: Phaser.GameObjects.Container;

    private speakerText: Phaser.GameObjects.Text | null = null;
    private messageText: Phaser.GameObjects.Text | null = null;
    private choiceTexts: Phaser.GameObjects.Text[] = [];
    private portrait: Portrait | null = null;

    private currentSpeaker: string | null = null;
    private currentMessage: string | null = null;
    private currentChoices: string[] = [];
    private currentPortraitUrl: string | null = null;
    private portraitPosition: "left" | "right" = "left"; // Configurable position
    private selectedIndex: number = 0;

    constructor(scene: Phaser.Scene, portraitPosition: "left" | "right" = "left") {
        this.scene = scene;
        this.container = scene.add.container(0, 0);
        this.portraitPosition = portraitPosition;
    }

    setSpeaker(speakerName: string) {
        this.currentSpeaker = speakerName;
    }

    setText(message: string) {
        this.currentMessage = message;
    }

    setChoices(choices: string[]) {
        this.currentChoices = choices;
    }

    setPortrait(portraitUrl: string | null) {
        this.currentPortraitUrl = portraitUrl;
    }

    setSelectedChoice(index: number) {
        this.selectedIndex = index;
        this.updateChoiceHighlight();
    }

    draw() {
        const themeManager = container.get<ThemeManager>(TYPES.ThemeManager);

        // Clear existing
        this.container.removeAll(true);
        this.speakerText = null;
        this.messageText = null;
        this.choiceTexts = [];

        // Destroy old portrait if exists
        if (this.portrait) {
            this.portrait.destroy();
            this.portrait = null;
        }

        // Position dialog box in the lower portion of the screen
        const portraitSize = 120;
        const hasPortrait = !!this.currentPortraitUrl;
        const portraitMargin = hasPortrait ? portraitSize + 40 : 0; // Space for portrait + margins

        // Adjust box position based on portrait position
        const boxX = this.portraitPosition === "left" && hasPortrait ? 20 + portraitMargin : 20;
        const boxY = this.scene.scale.height - 180; // 180px from bottom
        const boxWidth = this.scene.scale.width - 40 - portraitMargin; // Reduce width if portrait present
        const boxHeight = 160;

        // Background
        const bg = this.scene.add.rectangle(boxX, boxY, boxWidth, boxHeight, 0x000000, 0.8);
        bg.setOrigin(0, 0);
        this.container.add(bg);

        // Border
        const border = this.scene.add.rectangle(boxX, boxY, boxWidth, boxHeight, 0xffffff, 0);
        border.setOrigin(0, 0);
        border.setStrokeStyle(2, 0xffffff);
        this.container.add(border);

        let textY = boxY + 20;

        // Speaker name (if present)
        if (this.currentSpeaker) {
            this.speakerText = this.scene.add.text(boxX + 20, textY, this.currentSpeaker, {
                ...buildTextStyle(themeManager, {
                    color: themeManager.getColorString("accent.yellow"),
                    fontSize: "18px",
                }),
                fontStyle: "bold",
            });
            this.container.add(this.speakerText);
            textY += 30;
        }

        // Message text
        if (this.currentMessage) {
            this.messageText = this.scene.add.text(boxX + 20, textY, this.currentMessage, {
                ...buildTextStyle(themeManager, { fontSize: "16px" }),
                wordWrap: { width: boxWidth - 40 },
            });
            this.container.add(this.messageText);
            textY += this.messageText.height + 20;
        }

        // Choices (if present)
        if (this.currentChoices && this.currentChoices.length > 0) {
            this.currentChoices.forEach((choice, index) => {
                const choiceText = this.scene.add.text(boxX + 40, textY, `> ${choice}`, {
                    ...buildTextStyle(themeManager, { fontSize: "16px" }),
                });

                this.choiceTexts.push(choiceText);
                this.container.add(choiceText);
                textY += 25;
            });

            this.updateChoiceHighlight();
        }

        // Draw portrait if available
        if (this.currentPortraitUrl) {
            const config: PortraitConfig = {
                portraitUrl: this.currentPortraitUrl,
                position: this.portraitPosition,
                size: portraitSize,
            };
            this.portrait = new Portrait(this.scene, config);
            this.portrait.draw(boxY);
        }

        this.container.setDepth(1000);
    }

    private updateChoiceHighlight() {
        const themeManager = container.get<ThemeManager>(TYPES.ThemeManager);
        const selectedColor = themeManager.getColorString("accent.yellow");
        const normalColor = themeManager.getColorString("text.primary");

        this.choiceTexts.forEach((text, index) => {
            if (index === this.selectedIndex) {
                text.setColor(selectedColor);
                text.setFontStyle("bold");
            } else {
                text.setColor(normalColor);
                text.setFontStyle("normal");
            }
        });
    }

    destroy() {
        if (this.portrait) {
            this.portrait.destroy();
            this.portrait = null;
        }
        this.container.destroy();
    }
}
